import formattedDate from "../../lib/formatted-date";
import BlockText from "../block-content/block-text";
import Image from "gatsby-plugin-sanity-image";
import VideoThumbnail from "./styled-components/video-thumbnail";
import PlaceholderImage from "./styled-components/placeholder-image";
import PlaceholderVideoImage from "./styled-components/placeholder-video-image";
import { FaExternalLinkAlt } from "react-icons/fa";
import { SchemaVideoObject } from "../schema-video-object";
import PropTypes from "prop-types";

/**
 * Post Image
 * @param post
 * @param isHighlights
 * @param isHighlightsAlt
 * @param isRollinsInTheNews
 * @returns {JSX.Element}
 * @constructor
 */
const PostImage = ({
  post,
  isHighlights,
  isHighlightsAlt,
  isRollinsInTheNews,
}) => {
  const placeholderUrl = isRollinsInTheNews
    ? "https://cdn.sanity.io/images/qe2ul2l0/production/8476b7ae7731055941d2788ecb9231ebf801b11d-418x304.png"
    : "https://cdn.sanity.io/images/qe2ul2l0/production/7793b6d999f87700b1248703c3ede583859b516c-648x648.png";
  return (
    <>
      {!isHighlights &&
        !isHighlightsAlt &&
        post.mainImage &&
        post.mainImage.asset && (
          <Image
            asset={post.mainImage.asset}
            width={352}
            height={234}
            className="align-top mb-4"
            alt={post.mainImage.altText || ""}
          />
        )}
      {((!isHighlights &&
        !post.videoID &&
        !(post.mainImage && post.mainImage.asset)) ||
        isHighlightsAlt) && (
          <PlaceholderImage url={placeholderUrl} className="align-top mb-4" />
        )}
      {!isHighlights &&
        post.videoID &&
        !(post.mainImage && post.mainImage.asset) && (
          <PlaceholderVideoImage
            videoID={post.videoID}
            className="align-top mb-4"
          />
        )}
    </>
  );
};

/**
 * News Post
 * @param post
 * @param section
 * @param categorySlug
 * @returns {JSX.Element}
 * @constructor
 */
const NewsPost = ({ post, section, categorySlug }) => {
  const categories =
    Array.isArray(post.categories) && post.categories.length
      ? post.categories
        .filter(({ categorySlug }) => categorySlug)
        .map(({ categorySlug: { current } }) => current)
      : [];
  const isRollinsInTheNews =
    categorySlug === "rollins-in-the-news" ||
    categories.includes("rollins-in-the-news");
  const isHighlights = categorySlug === "highlights";
  const isHighlightsAlt = !isHighlights && categories.includes("highlights");
  let publishedAt = ``;
  try {
    publishedAt = formattedDate(post.publishedAt);
  } catch (e) {
    console.log("invalid date");
  }
  const slug = post.externalLink
    ? post.externalLink
    : section !== undefined
      ? `${section}news/${post.slug.current}`
      : post.slug.current;

  return (
    <div className="news-post md:w-1/2 lg:w-4/12 md:px-4 mb-12" key={post.id}>
      <a href={slug}>
        {post.videoID ? (
          <VideoThumbnail>
            <PostImage
              post={post}
              isHighlights={isHighlights}
              isHighlightsAlt={isHighlightsAlt}
              isRollinsInTheNews={isRollinsInTheNews}
            />
            <SchemaVideoObject
              name={post.title}
              description={post?.metaDescription || ""}
              thumbnail={
                post?.image?.asset?.url ||
                `https://img.youtube.com/vi/${post.videoID.trim()}/maxresdefault.jpg`
              }
              embedUrl={`https://www.youtube.com/embed/${post.videoID.trim()}?rel=0`}
              uploadDate={post?.publishedAt}
            />
          </VideoThumbnail>
        ) : (
          <PostImage
            post={post}
            isHighlights={isHighlights}
            isHighlightsAlt={isHighlightsAlt}
            isRollinsInTheNews={isRollinsInTheNews}
          />
        )}
      </a>
      <div className="news-post__copy">
        <p className="news-post__date text-gray-300 text-xs uppercase tracking-wide mb-1">
          {formattedDate(publishedAt)}
        </p>
        <h2 className="news-post__title font-bold tracking-tighter leading-tight text-blue-500 text-xl mb-2 md:mb-3 hover:text-blue-600 hover:underline">
          <a href={slug}>
            {post.title}
            {post.externalLink && (
              <FaExternalLinkAlt className="inline-block ml-1 pb-1" />
            )}
          </a>
        </h2>
        {!isHighlights && (
          <div className="news-post__intro text-sm">
            <BlockText blocks={post._rawIntro} />
          </div>
        )}
      </div>

    </div>
  );
};

NewsPost.propTypes = {
  post: PropTypes.object,
};

export default NewsPost;
